import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchExecutiveOverview } from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";
import { formatWeekDate } from "../utils/formatWeek";

const REFRESH_MS = 60000;

/* ------------------------------------------------------------------ */
/*  Хелперы                                                            */
/* ------------------------------------------------------------------ */

function moodRing(value) {
  if (value == null) return "var(--score-neutral)";
  if (value >= 70) return "var(--score-high)";
  if (value >= 45) return "var(--score-medium)";
  return "var(--score-low)";
}

function moodWord(value) {
  if (value == null) return "нет данных";
  if (value >= 80) return "Отличное";
  if (value >= 65) return "Хорошее";
  if (value >= 45) return "Среднее";
  if (value >= 30) return "Тревожное";
  return "Критичное";
}

function formatScore(v) {
  return v == null ? "—" : Number(v).toFixed(1);
}

function formatDayMonth(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

// Цвет для оценки 1..5 с учётом того, "хорошо ли когда высоко".
function scoreHealthColor(value, goodWhenHigh) {
  if (value == null) return "var(--score-neutral)";
  const ratio = Math.max(0, Math.min(1, (value - 1) / 4));
  const health = goodWhenHigh ? ratio : 1 - ratio;
  if (health >= 0.6) return "var(--score-high)";
  if (health >= 0.35) return "var(--score-medium)";
  return "var(--score-low)";
}

// Статус «системы» из реальных данных команды.
function systemStatus(data) {
  if (data.blockers_count > 0 || data.tasks_overdue > 0) {
    return { word: "ТРЕБУЕТ ДЕЙСТВИЙ", tone: "bad" };
  }
  if (data.attention_count > 0) {
    return { word: "ВНИМАНИЕ", tone: "warn" };
  }
  return { word: "НОМИНАЛ", tone: "ok" };
}

/* ------------------------------------------------------------------ */
/*  Анимация чисел (count-up)                                          */
/* ------------------------------------------------------------------ */

function useCountUp(target, { duration = 900, decimals = 0 } = {}) {
  const [value, setValue] = useState(target == null ? null : 0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target == null) {
      setValue(null);
      return undefined;
    }
    const from = fromRef.current ?? 0;
    const to = Number(target);
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  if (value == null) return "—";
  const factor = Math.pow(10, decimals);
  return (Math.round(value * factor) / factor).toFixed(decimals);
}

function CountUp({ value, decimals = 0, duration = 900 }) {
  return <>{useCountUp(value, { decimals, duration })}</>;
}

/* ------------------------------------------------------------------ */
/*  Иконки                                                             */
/* ------------------------------------------------------------------ */

const Icon = {
  attention: (
    <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
  ),
  blocker: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </>
  ),
  done: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  open: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 9h8M8 13h5" />
    </>
  ),
  overdue: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2 2M5 3 2 6m20 0-3-3" />
    </>
  ),
};

function TileIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {Icon[name]}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Тренд (моно-дельта)                                                */
/* ------------------------------------------------------------------ */

function Trend({ trend, suffix = "", compact = false }) {
  if (!trend || trend.delta == null) {
    return <span className="exec-trend flat">НЕТ ДАННЫХ</span>;
  }
  const delta = trend.delta;
  if (delta === 0) {
    return <span className="exec-trend flat">0.0 БЕЗ ИЗМ.</span>;
  }
  const rising = delta > 0;
  const positive = trend.good_when_high ? rising : !rising;
  const arrow = rising ? "▲" : "▼";
  const sign = rising ? "+" : "";
  return (
    <span className={`exec-trend ${positive ? "up" : "down"}`}>
      {arrow}&nbsp;{sign}
      {Number(delta).toFixed(1)}
      {suffix}
      {!compact && " К ПРОШ. НЕД."}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Сигнатура: осциллограф динамики индекса по неделям                 */
/* ------------------------------------------------------------------ */

const CHART_W = 1000;
const CHART_H = 240;
const CHART_PAD_X = 10;
const CHART_PAD_Y = 26;

function VitalsTrace({ history, ring }) {
  const pts = (history || []).filter((p) => p.value != null);
  if (pts.length < 2) {
    return <div className="exec-chart-empty">Недостаточно истории для динамики</div>;
  }

  const innerW = CHART_W - CHART_PAD_X * 2;
  const innerH = CHART_H - CHART_PAD_Y * 2;
  const x = (i) => CHART_PAD_X + (i * innerW) / (pts.length - 1);
  const y = (v) => CHART_PAD_Y + (1 - Math.max(0, Math.min(100, v)) / 100) * innerH;

  const coords = pts.map((p, i) => [x(i), y(p.value)]);
  const linePath = coords.map((c, i) => `${i ? "L" : "M"}${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(" ");
  const areaPath =
    `${linePath} L${coords[coords.length - 1][0].toFixed(1)} ${CHART_H} ` +
    `L${coords[0][0].toFixed(1)} ${CHART_H} Z`;
  const last = coords[coords.length - 1];

  const gridLines = [0, 25, 50, 75, 100];

  return (
    <svg className="exec-chart-svg" viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="vitalArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ring} stopOpacity="0.3" />
          <stop offset="100%" stopColor={ring} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="exec-chart-grid">
        {gridLines.map((g) => (
          <line key={g} x1={0} x2={CHART_W} y1={y(g)} y2={y(g)} className={g === 50 ? "mid" : ""} />
        ))}
      </g>
      <path className="exec-chart-area" d={areaPath} fill="url(#vitalArea)" />
      <path className="exec-chart-line" d={linePath} pathLength={1} style={{ stroke: ring }} />
      <circle className="exec-chart-halo" cx={last[0]} cy={last[1]} r={11} fill={ring} />
      <circle className="exec-chart-dot" cx={last[0]} cy={last[1]} r={5} fill={ring} />
    </svg>
  );
}

function VitalsMonitor({ value, delta, history }) {
  const ring = moodRing(value);
  const pts = (history || []).filter((p) => p.value != null);
  const firstWeek = pts.length ? pts[0].week_start : null;
  const lastWeek = pts.length ? pts[pts.length - 1].week_start : null;

  return (
    <section className="exec-monitor exec-enter" style={{ "--vital": ring }}>
      <div className="exec-monitor-grid" aria-hidden="true" />

      <div className="exec-monitor-readout">
        <div className="exec-eyebrow">Самочувствие команды</div>
        <div className="exec-vital" style={{ color: ring }}>
          <span className="exec-vital-num"><CountUp value={value} /></span>
          <span className="exec-vital-unit">/100</span>
        </div>
        <div className="exec-vital-meta">
          <span className="exec-vital-word" style={{ color: ring }}>{moodWord(value)}</span>
          <Trend trend={{ delta, good_when_high: true }} />
        </div>
        <div className="exec-vital-src">Индекс: энергия · коммуникация · дедлайны</div>
      </div>

      <div className="exec-chart">
        <span className="exec-chart-axis exec-chart-axis--top">100</span>
        <span className="exec-chart-axis exec-chart-axis--bot">0</span>
        <VitalsTrace history={history} ring={ring} />
        {firstWeek && lastWeek && (
          <div className="exec-chart-weeks">
            <span>{formatWeekDate(firstWeek)}</span>
            <span className="exec-chart-weeks-cap">динамика · {pts.length} нед.</span>
            <span>{formatWeekDate(lastWeek)}</span>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Канал (KPI)                                                        */
/* ------------------------------------------------------------------ */

function Channel({ label, trend, delay = 0 }) {
  const cur = trend?.current;
  const prev = trend?.previous;
  const goodWhenHigh = trend?.good_when_high ?? true;
  const color = scoreHealthColor(cur, goodWhenHigh);
  const fillPct = cur == null ? 0 : Math.max(0, Math.min(100, ((cur - 1) / 4) * 100));
  const prevPct = prev == null ? null : Math.max(0, Math.min(100, ((prev - 1) / 4) * 100));

  const [grown, setGrown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(fillPct));
    return () => cancelAnimationFrame(id);
  }, [fillPct]);

  return (
    <div className="exec-ch exec-enter" style={{ animationDelay: `${delay}ms` }}>
      <div className="exec-ch-top">
        <span className="exec-ch-label">{label}</span>
        <Trend trend={trend} compact />
      </div>
      <div className="exec-ch-val" style={{ color }}>
        <CountUp value={cur} decimals={1} />
        <i>/5</i>
      </div>
      <div className="exec-ch-bar">
        <div className="exec-ch-bar-fill" style={{ width: `${grown}%`, background: color }} />
        {prevPct != null && (
          <span className="exec-ch-bar-prev" style={{ left: `${prevPct}%` }}
            title={`Прошлая неделя: ${formatScore(prev)}`} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Блок «Руководитель» (ежедневные отчёты)                            */
/* ------------------------------------------------------------------ */

function ManagerBlock({ manager, delay = 0 }) {
  if (!manager) return null;
  const done = manager.reported_today;
  const total = manager.participants_count || 0;
  const todayCount = manager.reported_today_count || 0;
  const statusLabel =
    total > 1
      ? `СЕГОДНЯ ${todayCount}/${total}`
      : done
        ? "СЕГОДНЯ СДАН"
        : "СЕГОДНЯ НЕ СДАН";
  return (
    <section className="exec-mgr exec-enter" style={{ animationDelay: `${delay}ms` }}>
      <header className="exec-mgr-head">
        <span className="exec-panel-title">Ежедневные отчёты</span>
        <span className={`exec-mgr-status ${done ? "ok" : "warn"}`}>
          <span className="exec-status-led" />
          {statusLabel}
        </span>
      </header>
      <div className="exec-mgr-body">
        <Channel label="Загрузка" trend={manager.workload} delay={delay + 40} />
        <Channel label="Энергия" trend={manager.energy} delay={delay + 80} />
        <div className="exec-mgr-facts">
          <div className="exec-mgr-fact">
            <span className="exec-mgr-fact-val">{manager.reported_days}</span>
            <span className="exec-mgr-fact-label">отчётов за неделю</span>
          </div>
          <div className="exec-mgr-fact">
            <span className="exec-mgr-fact-val">{manager.clients_count}</span>
            <span className="exec-mgr-fact-label">клиентов за неделю</span>
          </div>
          <div className="exec-mgr-fact">
            <span className="exec-mgr-fact-val">{formatDayMonth(manager.last_report_date)}</span>
            <span className="exec-mgr-fact-label">последний отчёт</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Показание (readout)                                                */
/* ------------------------------------------------------------------ */

function Readout({ label, value, decimals = 0, suffix, text, tone = "default", delay = 0 }) {
  return (
    <div className={`exec-readout exec-readout--${tone} exec-enter`} style={{ animationDelay: `${delay}ms` }}>
      <div className="exec-readout-label">{label}</div>
      <div className="exec-readout-val">
        {text != null ? (
          <span className="exec-readout-text">{text}</span>
        ) : (
          <>
            <CountUp value={value} decimals={decimals} />
            {suffix && <i>{suffix}</i>}
          </>
        )}
      </div>
    </div>
  );
}

function CompletionReadout({ completed, total, pct, delay = 0 }) {
  const [grown, setGrown] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  return (
    <div className="exec-readout exec-readout--accent exec-enter" style={{ animationDelay: `${delay}ms` }}>
      <div className="exec-readout-label">Прошли опрос</div>
      <div className="exec-readout-val">
        <CountUp value={completed} />
        <i>/{total}</i>
      </div>
      <div className="exec-gaugebar">
        <div className="exec-gaugebar-fill" style={{ width: `${grown}%` }} />
      </div>
      <div className="exec-readout-sub"><CountUp value={Math.round(pct)} />% охват</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Списки                                                             */
/* ------------------------------------------------------------------ */

function EmptyState({ text }) {
  return (
    <div className="exec-empty">
      <span className="exec-empty-icon"><TileIcon name="done" /></span>
      <span>{text}</span>
    </div>
  );
}

function Avatar({ name, tone = "default" }) {
  const initials = (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  return <span className={`exec-avatar exec-avatar--${tone}`}>{initials}</span>;
}

function WatchPanel({ title, count, countTone, children, delay = 0 }) {
  return (
    <section className="exec-panel exec-enter" style={{ animationDelay: `${delay}ms` }}>
      <header className="exec-panel-head">
        <span className="exec-panel-title">{title}</span>
        {count != null && <span className={`exec-count-badge ${countTone}`}>{count}</span>}
      </header>
      <div className="exec-panel-body">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Главный компонент                                                  */
/* ------------------------------------------------------------------ */

export default function Executive() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback((showError = true) => {
    return fetchExecutiveOverview()
      .then((d) => {
        setData(d);
        setError(null);
        setUpdatedAt(new Date());
      })
      .catch((err) => {
        console.error("Failed to load executive overview:", err);
        if (showError) setError("Не удалось загрузить данные");
      });
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(false), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  if (error && !data) {
    return <ErrorRetry message={error} onRetry={load} />;
  }

  if (!data) {
    return <Loading text="Загрузка командного центра..." />;
  }

  const mood = data.mood_index;
  const moodDelta =
    mood != null && data.mood_prev != null ? mood - data.mood_prev : null;
  const completionPct = Math.max(0, Math.min(100, data.completion_rate || 0));
  const status = systemStatus(data);
  const updatedLabel = updatedAt
    ? updatedAt.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  return (
    <div className="exec-page exec-console">
      <div className="exec-topbar">
        <div className="exec-topbar-title">
          <span className="exec-eyebrow">Центр управления командой</span>
          <h2>Командный центр</h2>
          <div className="section-subtitle">
            Состояние команды одним взглядом · неделя с {formatWeekDate(data.week_start)}
          </div>
        </div>
        <div className="exec-meta">
          <span className={`exec-status exec-status--${status.tone}`}>
            <span className="exec-status-led" />
            Система: {status.word}
          </span>
          <span className="exec-clock">
            <span className="exec-live-dot" />
            Обн. {updatedLabel}
          </span>
          <button type="button" className="btn btn-sm" onClick={toggleFullscreen}>
            {isFullscreen ? "Свернуть" : "На весь экран"}
          </button>
        </div>
      </div>

      <div className="exec-hero">
        <VitalsMonitor value={mood} delta={moodDelta} history={data.mood_history} />
        <div className="exec-channels">
          <Channel label="Энергия" trend={data.energy} delay={60} />
          <Channel label="Коммуникация" trend={data.communication} delay={120} />
          <Channel label="Дедлайны" trend={data.deadlines} delay={180} />
          <Channel label="Нагрузка" trend={data.workload} delay={240} />
        </div>
      </div>

      <ManagerBlock manager={data.manager} delay={80} />

      <div className="exec-readouts">
        <CompletionReadout completed={data.completed_surveys} total={data.total_employees} pct={completionPct} delay={60} />
        <Readout label="Требуют внимания" value={data.attention_count}
          tone={data.attention_count > 0 ? "warn" : "ok"} delay={110} />
        <Readout label="Блокеров на неделе" value={data.blockers_count}
          tone={data.blockers_count > 0 ? "bad" : "ok"} delay={160} />
        <Readout label="Закрыто задач · 7д" value={data.tasks_completed_7d} tone="ok" delay={210} />
        <Readout label="Открытых задач" value={data.tasks_open} tone="default" delay={260} />
        <Readout label="Просрочено задач" value={data.tasks_overdue}
          tone={data.tasks_overdue > 0 ? "bad" : "ok"} delay={310} />
      </div>

      <div className="exec-columns">
        <WatchPanel title="Кто требует внимания"
          count={data.attention.length > 0 ? data.attention_count : null} countTone="warn" delay={120}>
          {data.attention.length === 0 ? (
            <EmptyState text="Все показатели в норме" />
          ) : (
            <div className="exec-list">
              {data.attention.map((a, i) => (
                <div className="exec-row exec-enter" key={a.employee_id} style={{ animationDelay: `${160 + i * 50}ms` }}>
                  <Avatar name={a.employee} tone="warn" />
                  <div className="exec-row-main">
                    <div className="exec-row-name">{a.employee}</div>
                    <div className="exec-tags">
                      {a.reasons.map((r, j) => (
                        <span className="exec-tag" key={j}>{r}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </WatchPanel>

        <WatchPanel title="Топ блокеров недели"
          count={data.top_blockers.length > 0 ? data.blockers_count : null} countTone="bad" delay={180}>
          {data.top_blockers.length === 0 ? (
            <EmptyState text="Блокеров нет — неделя идёт гладко" />
          ) : (
            <div className="exec-list">
              {data.top_blockers.map((b, i) => (
                <div className="exec-row exec-enter" key={b.response_id} style={{ animationDelay: `${220 + i * 50}ms` }}>
                  <Avatar name={b.employee} tone="bad" />
                  <div className="exec-row-main">
                    <div className="exec-row-name">{b.employee}</div>
                    <div className="exec-row-sub">{b.text}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </WatchPanel>

        <WatchPanel title="Клиенты в зоне риска"
          count={data.client_risks.length > 0 ? data.client_risks.length : null} countTone="bad" delay={240}>
          {data.client_risks.length === 0 ? (
            <EmptyState text="Рисковых клиентов нет" />
          ) : (
            <div className="exec-list">
              {data.client_risks.map((c, i) => {
                const parts = [];
                if (c.overloaded > 0) parts.push(`перегруз: ${c.overloaded}`);
                if (c.blocker_count > 0) parts.push(`блокеры: ${c.blocker_count}`);
                if (c.manager_days > 0) parts.push(`рук. включался ${c.manager_days} дн.`);
                if (c.avg_workload != null) parts.push(`ср. нагрузка ${formatScore(c.avg_workload)}`);
                return (
                  <div className="exec-row exec-enter" key={c.client_id} style={{ animationDelay: `${280 + i * 50}ms` }}>
                    <div className="exec-row-main">
                      <div className="exec-row-name">{c.client_name}</div>
                      <div className="exec-row-sub">{parts.join(" · ")}</div>
                    </div>
                    <span className="exec-risk-score">{c.risk_score}</span>
                  </div>
                );
              })}
            </div>
          )}
        </WatchPanel>
      </div>
    </div>
  );
}
